import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Card, Col, Collapse, Row, Space, Spin, Typography, theme } from 'antd';
import { captureLinesService, saldoIvaService } from '../../services/api';
import { interpretarAcuse } from '../../utils/acuseDeclaracion';
import ResponsiveTable from './ResponsiveTable';

const { Text } = Typography;

/**
 * Saldo a favor de IVA que se arrastra de meses anteriores.
 *
 * De dónde sale: cada acuse de declaración dice, por impuesto, si el periodo
 * cerró a cargo o a favor. El servidor DERIVA el acumulado sumando esos
 * registros; no hay saldo almacenado que pueda quedar descuadrado.
 *
 * QUIÉN LEE LOS PDF: este componente. El servidor no interpreta PDF —haría
 * falta una dependencia que el proyecto no quiere— así que cuando la consulta
 * devuelve acuses sin registro, se descargan aquí, se interpretan con pdf.js y
 * se reportan. Ocurre DESPUÉS de pintar el saldo y en segundo plano: la
 * pantalla no espera a que se lean hasta doce PDF.
 *
 * ES ESTIMACIÓN, NO CIFRA FISCAL, y se dice en pantalla. Supone que el
 * contribuyente acreditó todo lo posible cada mes y que no pidió devolución ni
 * compensó; si recuperó un saldo por otra vía, el acumulado queda POR ENCIMA
 * del real. Esa es la dirección peligrosa —sugiere no pagar cuando sí hay que
 * hacerlo—, y por eso la advertencia es permanente y no un aviso que se cierra.
 */

/** Cuántos acuses se leen a la vez. Doce PDF en paralelo cuelgan la pestaña. */
const PARALELISMO = 2;

const formatCurrency = (valor) =>
  Number(valor || 0).toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });

const MESES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];

/**
 * `2026-07` -> `julio 2026`. El usuario piensa en meses, no en claves.
 *
 * Los nombres van aquí y no por `dayjs.format('MMMM')` porque el proyecto no
 * carga el locale español de dayjs: pedirlo devolvería "July". Importarlo
 * global por esta pantalla cambiaría el formato de fechas de toda la
 * aplicación.
 */
const nombrePeriodo = (periodo) => {
  const m = /^(\d{4})-(\d{2})$/.exec(String(periodo || ''));
  if (!m) return periodo;
  const mes = MESES[Number(m[2]) - 1];
  return mes ? `${mes} ${m[1]}` : periodo;
};

const MOTIVO_HUECO = {
  sin_acuse: 'sin acuse cargado',
  sin_interpretar: 'acuse no legible',
};

/**
 * Lee los acuses que aún no tienen registro y los reporta.
 *
 * DISTINGUE DOS FALLOS que antes iban al mismo `catch` vacío:
 *
 * - **No se pudo interpretar** el PDF: cuenta como hueco, y el servidor ya lo
 *   reporta como tal en la siguiente consulta. No hace falta decir nada más.
 * - **Se interpretó pero no se pudo registrar**: eso no produce un hueco
 *   visible, produce una CANTIDAD A PAGAR EQUIVOCADA en una pantalla que se ve
 *   normal. Hay que decirlo.
 *
 * Meterlos en el mismo saco fue justo lo que escondió un 403 del perfil visor
 * durante días: el síntoma era una cifra mal, sin nada que apuntara a la causa.
 *
 * @returns {Promise<{registrados: number, sinRegistrar: number}>}
 */
const rellenar = async (companyId, pendientes) => {
  let siguiente = 0;
  let registrados = 0;
  let sinRegistrar = 0;

  const trabajador = async () => {
    while (siguiente < pendientes.length) {
      const acuse = pendientes[siguiente];
      siguiente += 1;

      let lectura = null;
      try {
        const resp = await captureLinesService.downloadByPeriod(
          companyId,
          acuse.period,
          acuse.consecutivo
        );
        lectura = await interpretarAcuse(
          new Blob([resp.data], { type: 'application/pdf' })
        );
      } catch (e) {
        // No se pudo leer: hueco, y el servidor ya lo dice.
        continue;
      }

      if (!lectura.conceptos.length) continue;

      try {
        await saldoIvaService.reportarImportes(companyId, acuse.capture_line_id, {
          rfc: lectura.rfc,
          period: lectura.period,
          conceptos: lectura.conceptos,
        });
        registrados += 1;
      } catch (e) {
        // Se entendió el documento y no se pudo guardar. Esto sí se avisa.
        sinRegistrar += 1;
      }
    }
  };

  await Promise.all(
    Array.from({ length: Math.min(PARALELISMO, pendientes.length) }, trabajador)
  );

  return { registrados, sinRegistrar };
};

const SaldoFavorIvaPanel = ({ companyId, period, ivaACargoEstimado = 0, onDeclarado }) => {
  const { token } = theme.useToken();
  const [saldo, setSaldo] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [rellenando, setRellenando] = useState(false);
  // Acuses que se entendieron pero no se pudieron registrar. No es lo mismo que
  // un acuse ilegible, y por eso no comparte aviso con los huecos.
  const [sinRegistrar, setSinRegistrar] = useState(0);
  const [error, setError] = useState('');
  const vivoRef = useRef(true);

  useEffect(() => {
    vivoRef.current = true;
    return () => {
      vivoRef.current = false;
    };
  }, []);

  const consultar = useCallback(async () => {
    if (!companyId || !period) {
      setSaldo(null);
      return;
    }

    setCargando(true);
    setError('');
    setSinRegistrar(0);

    try {
      const datos = await saldoIvaService.get(companyId, period);
      if (!vivoRef.current) return;
      setSaldo(datos);

      const pendientes = datos?.acuses_sin_registro || [];
      if (!pendientes.length) return;

      // El saldo ya está pintado; a partir de aquí es trabajo de fondo.
      setRellenando(true);
      const { registrados, sinRegistrar } = await rellenar(companyId, pendientes);
      if (!vivoRef.current) return;

      // Un solo aviso por consulta, no uno por acuse: cuando la causa es el
      // plan fallan todos, y cinco avisos idénticos esconden el mensaje en vez
      // de reforzarlo.
      setSinRegistrar(sinRegistrar);

      if (registrados > 0) {
        setSaldo(await saldoIvaService.get(companyId, period));
      }
    } catch (e) {
      if (vivoRef.current) setError('No se pudo consultar el saldo a favor de IVA');
    } finally {
      if (vivoRef.current) {
        setCargando(false);
        setRellenando(false);
      }
    }
  }, [companyId, period]);

  useEffect(() => {
    consultar();
  }, [consultar]);

  /**
   * Informa hacia arriba lo declarado del periodo, para que la estimación
   * presente esas cantidades en vez de calcularlas sobre CFDI.
   *
   * Un número que se ve arriba nace aquí abajo, y eso no es bonito. La
   * alternativa —subir la consulta a la pantalla— arrastraría también el
   * relleno: descarga, interpretación y paralelismo acotado, en una pantalla
   * que ya hace cinco peticiones y no sabe nada de PDF.
   *
   * Se comprueba `saldo.period === period` porque al cambiar de mes la
   * respuesta anterior sigue en estado hasta que llega la nueva: sin eso, la
   * pantalla mostraría un momento lo declarado del mes anterior.
   */
  useEffect(() => {
    if (!onDeclarado) return;

    const vigente =
      saldo?.period === period && saldo?.periodo_consultado?.interpretado
        ? saldo.periodo_consultado
        : null;

    onDeclarado(vigente);
  }, [saldo, period, onDeclarado]);

  if (!companyId || !period) return null;

  if (cargando && !saldo) {
    return (
      <Card title="Saldo a favor de IVA">
        <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
          <Spin />
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card title="Saldo a favor de IVA">
        <Alert message={error} type="warning" showIcon />
      </Card>
    );
  }

  if (!saldo) return null;

  const acumulado = Number(saldo.saldo_favor_iva || 0);
  const declarado = saldo.periodo_consultado;
  const ivaDeclarado = declarado?.impuestos?.iva || null;

  // El periodo consultado queda fuera del acumulado a propósito: si ya está
  // declarado, aplicarle además el saldo a una estimación contaría dos veces.
  const aCargo = Math.max(0, Number(ivaACargoEstimado || 0));
  const acreditado = Math.min(acumulado, aCargo);
  const resultado = aCargo - acreditado;
  const restante = acumulado - acreditado;

  const huecos = saldo.huecos || [];
  const rango = saldo.rango || {};

  const columnas = [
    {
      title: 'Periodo',
      dataIndex: 'period',
      key: 'period',
      render: (valor) => nombrePeriodo(valor),
    },
    {
      title: 'Declarado',
      dataIndex: 'importe',
      key: 'importe',
      align: 'right',
      render: (valor, fila) => (
        <Text style={{ color: fila.signo === 'favor' ? token.colorSuccess : undefined }}>
          {formatCurrency(valor)} {fila.signo === 'favor' ? 'a favor' : 'a cargo'}
        </Text>
      ),
    },
    {
      title: 'Acreditado',
      dataIndex: 'acreditado',
      key: 'acreditado',
      align: 'right',
      render: (valor) => formatCurrency(valor),
    },
    {
      title: 'Saldo tras el periodo',
      dataIndex: 'saldo_despues',
      key: 'saldo_despues',
      align: 'right',
      render: (valor) => <Text strong>{formatCurrency(valor)}</Text>,
    },
  ];

  return (
    <Card
      title="Saldo a favor de IVA"
      extra={
        rellenando ? (
          <Space size={6}>
            <Spin size="small" />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Leyendo acuses…
            </Text>
          </Space>
        ) : null
      }
    >
      <Space direction="vertical" style={{ width: '100%' }} size={12}>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <div
              style={{
                borderRadius: 8,
                padding: '12px 16px',
                border: `1px solid ${token.colorSuccessBorder}`,
                background: token.colorSuccessBg,
              }}
            >
              <Text type="secondary">Acumulado de meses anteriores</Text>
              <div style={{ fontSize: 24, fontWeight: 600, color: token.colorSuccess }}>
                {formatCurrency(acumulado)}
              </div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {rango.desde
                  ? `De ${nombrePeriodo(rango.desde)} a ${nombrePeriodo(rango.hasta)}`
                  : 'Sin periodos anteriores declarados'}
              </Text>
            </div>
          </Col>

          <Col xs={24} md={12}>
            {declarado ? (
              // Ya declarado: se muestra lo que dice el acuse, no una
              // estimación con el saldo aplicado encima.
              <div style={{ borderRadius: 8, padding: '12px 16px', border: `1px solid ${token.colorSplit}` }}>
                <Text type="secondary">{nombrePeriodo(saldo.period)} ya está declarado</Text>
                <div style={{ marginTop: 4 }}>
                  {ivaDeclarado ? (
                    <Text strong style={{ fontSize: 18 }}>
                      {formatCurrency(ivaDeclarado.importe)}{' '}
                      {ivaDeclarado.signo === 'favor' ? 'a favor' : 'a cargo'}
                    </Text>
                  ) : (
                    // Interpretado sin IVA no es lo mismo que sin interpretar:
                    // el acuse se leyó y no declara ese impuesto.
                    <Text type="secondary">
                      {declarado.interpretado
                        ? 'El acuse no declara IVA'
                        : 'Acuse aún sin interpretar'}
                    </Text>
                  )}
                </div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Según el acuse presentado ante el SAT
                </Text>
              </div>
            ) : (
              // La operación COMPLETA, no solo la cifra final: sin ver de dónde
              // sale el resultado no hay forma de revisarlo.
              <div style={{ borderRadius: 8, padding: '12px 16px', border: `1px solid ${token.colorSplit}` }}>
                <Row justify="space-between">
                  <Text type="secondary">IVA a cargo estimado</Text>
                  <Text>{formatCurrency(aCargo)}</Text>
                </Row>
                <Row justify="space-between">
                  <Text type="secondary">Saldo acreditado</Text>
                  <Text style={{ color: token.colorSuccess }}>−{formatCurrency(acreditado)}</Text>
                </Row>
                <div style={{ borderTop: `1px solid ${token.colorSplit}`, marginTop: 8, paddingTop: 8 }}>
                  <Row justify="space-between">
                    <Text strong>Quedaría por pagar</Text>
                    <Text strong style={{ fontSize: 16 }}>{formatCurrency(resultado)}</Text>
                  </Row>
                  <Row justify="space-between">
                    <Text type="secondary" style={{ fontSize: 12 }}>Saldo restante</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>{formatCurrency(restante)}</Text>
                  </Row>
                </div>
              </div>
            )}
          </Col>
        </Row>

        {/* Se entendió el documento y no se pudo guardar. Va aparte del aviso
            de huecos porque la causa es otra y la solución también: ahí falta
            un acuse o no se deja leer; aquí sí se leyó. Y el aviso NO sustituye
            al contenido: el saldo y el desglose siguen siendo correctos para lo
            que el servidor sabe. */}
        {sinRegistrar > 0 ? (
          <Alert
            type="warning"
            showIcon
            message="No se pudo guardar lo que dice el acuse"
            description={
              sinRegistrar === 1
                ? 'Se leyó un acuse pero no se pudo registrar, así que lo que ves sigue siendo la estimación sobre tus CFDI y no lo declarado. Si tu plan es de solo lectura, coméntalo con tu gestor.'
                : `Se leyeron ${sinRegistrar} acuses pero no se pudieron registrar, así que lo que ves sigue siendo la estimación sobre tus CFDI y no lo declarado. Si tu plan es de solo lectura, coméntalo con tu gestor.`
            }
          />
        ) : null}

        {huecos.length > 0 ? (
          <Alert
            type="warning"
            showIcon
            message="Saldo incompleto"
            description={
              <>
                Falta información de{' '}
                {huecos
                  .map((h) => `${nombrePeriodo(h.period)} (${MOTIVO_HUECO[h.motivo] || h.motivo})`)
                  .join(', ')}
                . Sobre una cadena con huecos el saldo no es firme.
                {rango.tope_aplicado
                  ? ` Además, solo se buscaron los últimos ${rango.meses_atras} meses: si arrastras saldo de antes, no está contado.`
                  : ''}
              </>
            }
          />
        ) : rango.tope_aplicado ? (
          <Alert
            type="info"
            showIcon
            message={`Solo se buscaron los últimos ${rango.meses_atras} meses`}
            description="Si arrastras saldo a favor de más atrás, no está contado en esta cifra."
          />
        ) : null}

        {(saldo.desglose || []).length > 0 ? (
          <Collapse
            ghost
            items={[
              {
                key: 'desglose',
                label: 'Ver de dónde sale el saldo',
                children: (
                  <ResponsiveTable
                    columns={columnas}
                    dataSource={(saldo.desglose || []).map((fila) => ({
                      key: fila.period,
                      ...fila,
                    }))}
                    size="small"
                    pagination={false}
                  />
                ),
              },
            ]}
          />
        ) : null}

        <Alert
          type="info"
          showIcon
          message="Es una estimación, no una declaración"
          description="El saldo sale de los acuses presentados y supone que acreditaste todo lo posible cada mes. No contempla devoluciones ni compensaciones: si recuperaste un saldo por otra vía, esta cifra queda por encima de la real."
        />
      </Space>
    </Card>
  );
};

export default SaldoFavorIvaPanel;
